import React, { useMemo, useState } from "react";
import { Calculator, ArrowRight, WhatsappLogo } from "@phosphor-icons/react";
import { useI18n } from "@/context/LangContext";

/**
 * A universal, honest ROI mini-calculator embedded on product pages.
 * Inputs: team size, current tool spend, avg. hours saved per staff/week.
 * Outputs: estimated monthly savings + payback in months.
 *
 * The math is intentionally conservative (uses local hourly rate presets)
 * so the number stays defensible on a sales call.
 */

const HOURLY_RATE_USD = {
  IN: 6, AE: 22, NG: 8, ZW: 5, ZM: 5, BW: 7, SA: 18, KE: 6, OM: 15, DE: 45, UK: 40,
};

export default function RoiCalculator({ productTitle }) {
  const { t } = useI18n();
  const [team, setTeam] = useState(20);
  const [tool, setTool] = useState(400);      // current monthly tool spend, USD
  const [hoursSaved, setHoursSaved] = useState(4); // hours saved per staff per week
  const [region, setRegion] = useState("AE");

  const rate = HOURLY_RATE_USD[region] || 15;

  const { laborSaved, toolSaved, monthlySavings, annualSavings, paybackMonths } = useMemo(() => {
    const weeklyLabor = team * hoursSaved * rate;   // USD
    const laborSaved = Math.round(weeklyLabor * 4.33);
    const toolSaved = Math.round(tool * 0.6);       // assume 60% of legacy tool spend replaced
    const monthlySavings = laborSaved + toolSaved;
    const annualSavings = monthlySavings * 12;
    // Assume a mid-tier license around $600/mo for payback math (illustrative)
    const paybackMonths = monthlySavings > 0 ? Math.max(1, Math.round(600 / monthlySavings)) : null;
    return { laborSaved, toolSaved, monthlySavings, annualSavings, paybackMonths };
  }, [team, tool, hoursSaved, rate]);

  const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

  return (
    <section id="roi" data-testid="roi-calculator" className="mt-14 border border-[color:var(--ink)] bg-white">
      <div className="border-b border-[color:var(--line)] px-6 py-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-[color:var(--brand)] text-white flex items-center justify-center">
            <Calculator size={18} weight="bold" />
          </div>
          <div>
            <div className="eyebrow">{t("roi.eyebrow")}</div>
            <div className="font-display font-bold text-lg">{t("roi.title", { product: productTitle })}</div>
          </div>
        </div>
        <select
          data-testid="roi-region"
          value={region}
          onChange={(e) => setRegion(e.target.value)}
          className="font-mono text-xs uppercase tracking-widest border border-[color:var(--line)] px-3 py-2 bg-white"
        >
          {Object.entries({ IN: "India", AE: "UAE", NG: "Nigeria", ZW: "Zimbabwe", ZM: "Zambia", BW: "Botswana", SA: "Saudi Arabia", KE: "Kenya", OM: "Oman", DE: "Germany", UK: "UK" }).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      <div className="grid md:grid-cols-2">
        <div className="p-6 border-b md:border-b-0 md:border-r border-[color:var(--line)] space-y-5">
          <Slider
            label={t("roi.team")}
            testid="roi-team"
            value={team} onChange={setTeam}
            min={5} max={500} step={5}
            unit={team === 1 ? "person" : "people"}
          />
          <Slider
            label={t("roi.tool")}
            testid="roi-tool"
            value={tool} onChange={setTool}
            min={0} max={5000} step={50}
            prefix="US$"
          />
          <Slider
            label={t("roi.hours")}
            testid="roi-hours"
            value={hoursSaved} onChange={setHoursSaved}
            min={1} max={20} step={1}
            unit="hrs"
          />
          <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-500 pt-2 border-t border-[color:var(--line)]">
            {t("roi.disclaimer", { region, rate })}
          </p>
        </div>

        <div className="p-6 md:p-8 bg-[color:var(--brand)]/5 flex flex-col justify-between">
          <div className="space-y-4">
            <Stat label={t("roi.labour")} value={fmt(laborSaved)} />
            <Stat label={t("roi.tools")} value={fmt(toolSaved)} />
            <div className="border-t border-[color:var(--brand)]/40 pt-4">
              <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-600">{t("roi.total")}</div>
              <div data-testid="roi-monthly" className="mt-1 font-display font-black text-4xl md:text-5xl text-[color:var(--brand)]">{fmt(monthlySavings)}</div>
              <div data-testid="roi-annual" className="mt-1 font-mono text-xs text-neutral-600">{t("roi.year", { annual: fmt(annualSavings), months: paybackMonths || "—", s: paybackMonths === 1 ? "" : "s" })}</div>
            </div>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <a
              href={`https://wa.me/7054986771?text=${encodeURIComponent(`I ran the ROI calculator for ${productTitle} — team ${team}, savings ≈ ${fmt(monthlySavings)}/mo. Please share pricing.`)}`}
              target="_blank" rel="noreferrer"
              data-testid="roi-cta-whatsapp"
              className="btn-brand text-sm py-2.5 px-4"
            >
              <WhatsappLogo size={14} weight="fill" /> {t("roi.send")}
            </a>
            <a href="#contact-cta" data-testid="roi-cta-scope" className="btn-ghost text-sm py-2.5 px-4">
              {t("roi.pilot")} <ArrowRight size={14} weight="bold" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}

function Slider({ label, testid, value, onChange, min, max, step, unit, prefix }) {
  return (
    <label className="block">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-500">{label}</span>
        <span data-testid={`${testid}-value`} className="font-display font-bold text-sm">
          {prefix}{value.toLocaleString()} {unit || ""}
        </span>
      </div>
      <input
        type="range"
        data-testid={testid}
        min={min} max={max} step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="mt-2 w-full accent-[color:var(--brand)]"
      />
    </label>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-widest text-neutral-600">{label}</div>
      <div className="mt-0.5 font-display font-bold text-lg">{value}</div>
    </div>
  );
}
